import React from 'react';
import { WrappedComponentProps, injectIntl } from 'react-intl';

import { Button, TextButton } from '@xingternal/button';
import { Checkbox } from '@xingternal/checkbox';
import { Label } from '@xingternal/label';
import { List, ListItem } from '@xingternal/list';
import { PopUpWindow } from '@xingternal/pop-up-window';
import { RadioButton } from '@xingternal/radio-button';
import { RadioButtonGroup } from '@xingternal/radio-button-group';
import { FormField } from '@xingternal/text-field';
import { BodyCopy, Headline } from '@xingternal/typography';

import { JobReportReason, JobReportableField } from '@/graphql/generated';

import styles from './report-job-ad-lightbox-component.css';

interface Props {
  isShown: boolean;
  reason: JobReportReason;
  wrongFields?: JobReportableField[];
  comment?: string;
  onReport: () => void;
  onCancel: () => void;
  onReasonChange: (reason: JobReportReason) => void;
  onWrongFieldChange: (field: JobReportableField) => void;
  onCommentChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

const ReportJobAdLightbox: React.FC<Props & WrappedComponentProps> = ({
  isShown,
  reason: reportReason,
  wrongFields: reportableField,
  comment,
  onReport,
  onCancel,
  onReasonChange,
  onWrongFieldChange,
  onCommentChange,
  intl,
}) => (
  <PopUpWindow isShown={isShown} hide={onCancel} onDimmerClick={onCancel} className={styles.container}>
    <Headline level="xlarge">{intl.formatMessage({ id: 'JOB_POSTING_REPORT_LIGHTBOX_TITLE' })}</Headline>

    <BodyCopy className={styles.paragraph}>
      {intl.formatMessage({ id: 'JOB_POSTING_REPORT_LIGHTBOX_DESCRIPTION' })}
    </BodyCopy>

    <RadioButtonGroup>
      {Object.values(JobReportReason).map((reason: JobReportReason) => (
        <Label key={reason}>
          <RadioButton
            value={reason}
            on={reportReason === reason}
            onChange={(): void => onReasonChange(reason)}
            name="reasons"
            className={styles.radioButton}
          />
          {intl.formatMessage({ id: `JOB_POSTING_REPORT_FORM_${reason}` })}
        </Label>
      ))}
    </RadioButtonGroup>

    <List className={styles.wrongFields}>
      {Object.values(JobReportableField).map((field: JobReportableField) => (
        <ListItem key={field}>
          <Label>
            <Checkbox
              on={reportableField && reportableField.includes(field)}
              onChange={(): void => onWrongFieldChange(field)}
              className={styles.checkbox}
            />
            {intl.formatMessage({
              id: `JOB_POSTING_REPORT_FORM_${
                field === 'OTHER' ? 'OTHER_ISSUE' : `WRONG_${field === 'EMPLOYMENT_TYPE' ? 'JOB_TYPE' : field}`
              }`,
            })}
          </Label>
        </ListItem>
      ))}
    </List>

    <Label htmlFor="comment" className={styles.commentLabel}>
      {intl.formatMessage({ id: 'JOB_POSTING_REPORT_FORM_ADD_COMMENT' })}
    </Label>
    <FormField
      name="comment"
      placeholder={intl.formatMessage({ id: 'JOB_POSTING_REPORT_FORM_COMMENT_PLACEHOLDER' })}
      value={comment}
      onChange={onCommentChange}
      multiline
      size="large"
      minRows={5}
    />

    <div className={styles.buttons}>
      <TextButton size="medium" className={styles.cancelButton} onClick={onCancel}>
        {intl.formatMessage({ id: 'JOB_POSTING_REPORT_CANCEL_BUTTON' })}
      </TextButton>
      <Button variant="primary" size="medium" className={styles.confirmButton} onClick={onReport}>
        {intl.formatMessage({ id: 'JOB_POSTING_REPORT_MOBILE_REPORT_BUTTON' })}
      </Button>
    </div>
  </PopUpWindow>
);

export default injectIntl(ReportJobAdLightbox);
